import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IClasse, defaultValue } from 'app/shared/model/classe.model';
const initialState: EntityState<IClasse> = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IClasse>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/classes';

// Actions

export const getClasses = createAsyncThunk('classe/fetch_classe_list', async (ecoleId: string | number) => {
  const requestUrl = `${apiUrl}?ecoleId=${ecoleId}`;
  return axios.get<IClasse[]>(requestUrl);
});

export const getClassesProf = createAsyncThunk(
  'classe/fetch_classe_list',
  async ({ ecoleId, profId }: { ecoleId: string | number; profId: string }) => {
    const requestUrl = `${apiUrl}/prof/${ecoleId}?profId=${profId}`;
    return axios.get<IClasse[]>(requestUrl);
  }
);

export const getClasse = createAsyncThunk(
  'classe/fetch_classe',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IClasse>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createClasse = createAsyncThunk(
  'classe/create_classe',
  async (entity: IClasse, thunkAPI) => {
    const result = await axios.post<IClasse>(`${apiUrl}/${entity.ecoleId}`, cleanEntity(entity));
    thunkAPI.dispatch(getClasses(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateClasse = createAsyncThunk(
  'classe/update_classe',
  async (entity: IClasse, thunkAPI) => {
    const result = await axios.put<IClasse>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getClasses(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteClasse = createAsyncThunk(
  'classe/delete_classe',
  async (entity: IClasse, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<IClasse>(requestUrl);
    thunkAPI.dispatch(getClasses(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export type ClassetState = Readonly<typeof initialState>;

// slice

export const ClasseSlice = createSlice({
  name: 'classe',
  initialState: initialState as ClassetState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getClasse.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteClasse.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(createClasse, updateClasse), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getClasses, getClasse), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isFulfilled(getClasses), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addMatcher(isFulfilled(getClassesProf), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addMatcher(isPending(createClasse, updateClasse, deleteClasse), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ClasseSlice.actions;

// Reducer
export default ClasseSlice.reducer;
